import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EMPTY_TRIGGER_STATS } from '../../utils/workflowTriggerUtils';
import {
  computeFixedPopoverPosition,
  WORKFLOW_STATS_POPOVER_WIDTH,
} from '../../utils/workflowPopoverPosition';

const WorkflowTriggerStatsPopover = ({
  isOpen,
  onClose,
  anchorEl,
  anchorRef,
  stats = EMPTY_TRIGGER_STATS,
  conditionText,
  onViewDetailed,
  loading = false,
}) => {
  const panelRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const resolveAnchor = useCallback(() => (
    anchorEl || anchorRef?.current || null
  ), [anchorEl, anchorRef]);

  const updatePosition = useCallback(() => {
    const anchor = resolveAnchor();
    if (!anchor) return;
    const anchorRect = anchor.getBoundingClientRect();
    const estimatedHeight = panelRef.current?.offsetHeight || 220;
    setPosition(computeFixedPopoverPosition(anchorRect, { estimatedHeight }));
  }, [resolveAnchor]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition, stats, conditionText, loading]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleScrollOrResize = () => {
      const anchor = resolveAnchor();
      if (!anchor || !document.body.contains(anchor)) {
        onClose?.();
        return;
      }
      updatePosition();
    };

    const handleClickOutside = (event) => {
      if (panelRef.current?.contains(event.target)) return;
      const anchor = resolveAnchor();
      if (anchor?.contains(event.target)) return;
      onClose?.();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);
    document.addEventListener('pointerdown', handleClickOutside, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      document.removeEventListener('pointerdown', handleClickOutside, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, resolveAnchor, updatePosition]);

  if (!isOpen || typeof document === 'undefined') return null;

  const popover = (
    <div
      ref={panelRef}
      data-workflow-editor-ignore-pan
      className="fixed z-[180] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
      style={{
        top: position.top,
        left: position.left,
        width: WORKFLOW_STATS_POPOVER_WIDTH,
      }}
      data-testid="workflow-trigger-stats-popover"
    >
      <div className="border-b border-slate-100 px-4 py-3">
        <h4 className="text-sm font-semibold text-slate-900">Trigger enrollments</h4>
      </div>

      <div className="grid grid-cols-3 gap-2 px-4 py-4">
        {[
          { label: 'Attempted', value: stats.attempted },
          { label: 'Matched', value: stats.matched },
          { label: 'Unmatched', value: stats.unmatched },
        ].map((item) => (
          <div key={item.label} className="text-center">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              {item.label}
            </div>
            <div className="mt-1 text-2xl font-semibold text-slate-900">
              {loading ? '—' : item.value}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 px-4 py-3">
        <button
          type="button"
          onClick={onViewDetailed}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
          data-testid="workflow-trigger-view-detailed-stats"
        >
          View detailed stats
        </button>
      </div>

      {conditionText ? (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-3">
          <p className="text-xs font-medium text-slate-500">Start automation when</p>
          <p className="mt-1 text-sm text-slate-700">{conditionText}</p>
        </div>
      ) : null}
    </div>
  );

  return createPortal(popover, document.body);
};

export default WorkflowTriggerStatsPopover;
